import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BannerSlider from "@/components/BannerSlider";
import SEO from "@/components/SEO";
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Award } from "lucide-react";

const absoluteImageUrl = (value) =>
  typeof value === "string" && value.startsWith("/api/")
    ? `${BACKEND_URL}${value}`
    : value;

const TRUST = [
  { icon: Truck, title: "Free Shipping", desc: "On orders ₹1,499 and above" },
  { icon: ShieldCheck, title: "Secure Payments", desc: "UPI · Cards · Net Banking" },
  { icon: RotateCcw, title: "7-Day Returns", desc: "Hassle-free exchanges" },
  { icon: Award, title: "Handcrafted Quality", desc: "From India's finest artisans" },
];

const TAG_SECTIONS = [
  { tag: "bestseller", title: "Bestsellers", eyebrow: "Loved by our customers" },
  { tag: "new-arrival", title: "New Arrivals", eyebrow: "Just in" },
  { tag: "festive", title: "Festive Edit", eyebrow: "For special occasions" },
];

export default function Home() {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tagSections, setTagSections] = useState({});

  const [homepageSettings, setHomepageSettings] = useState(null);
  const heroImageRef = useRef(null);
  const heroTouchStartX = useRef(null);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data));

    api.get("/homepage/settings")
      .then((r) => setHomepageSettings(r.data || {}))
      .catch(() => setHomepageSettings({}));
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => !c.parent_slug)));
    // Fetch products for each tag section
    TAG_SECTIONS.forEach(({ tag }) => {
      api.get(`/products?tag=${tag}&limit=8`).then((r) => {
        setTagSections((prev) => ({ ...prev, [tag]: r.data }));
      });
    });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!heroImageRef.current) return;
      heroImageRef.current.style.transform = `translateY(${window.scrollY * 0.12}px)`;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const home = {
    hero_enabled: true,
    hero_eyebrow: "Nityavastra / New Season",
    hero_title: "DRAPE YOUR STORY.",
    hero_subtitle: "Indian textiles. New expressions. Entirely yours.",
    hero_primary_cta_text: "Shop Sarees",
    hero_primary_cta_link: "/shop?category=sarees",
    hero_secondary_cta_text: "Explore Collection",
    hero_secondary_cta_link: "/shop",
    hero_image: "",
    hero_images: [
      "https://images.pexels.com/photos/32982848/pexels-photo-32982848.jpeg?auto=compress&cs=tinysrgb&h=1400&w=1000",
      "https://images.pexels.com/photos/33150440/pexels-photo-33150440.jpeg?auto=compress&cs=tinysrgb&h=1400&w=1000",
      "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=85",
    ],
    hero_secondary_image: "",
    hero_badge: "New Season '26",
    hero_caption: "Six Yards of Infinite Moods.",
    trust_strip_enabled: true,
    categories_enabled: true,
    bestseller_enabled: true,
    new_arrivals_enabled: true,
    festive_enabled: true,
    categories_title: "Shop by Category",
    categories_subtitle: "Find your expression.",

    shop_by_mood_enabled: true,
    shop_by_mood_title: "Shop by Mood",
    shop_by_mood_image_1: "",
    shop_by_mood_image_2: "",
    shop_by_mood_image_3: "",

    textile_story_enabled: true,
    textile_story_title: "FROM LOOM TO YOU.",
    textile_story_text: "",
    textile_story_image: "",

    community_enabled: true,
    community_title: "WORN YOUR WAY",
    community_image_1: "",
    community_image_2: "",
    community_image_3: "",

    ...(homepageSettings || {}),
  };

  const heroSlides =
    Array.isArray(home.hero_images) && home.hero_images.filter(Boolean).length
      ? home.hero_images.filter(Boolean).slice(0, 4)
      : [
          home.hero_image ||
            "https://images.pexels.com/photos/32982848/pexels-photo-32982848.jpeg?auto=compress&cs=tinysrgb&h=1400&w=1000",
        ];

  const showPreviousHeroSlide = () => {
    setHeroSlideIndex((current) =>
      heroSlides.length
        ? (current - 1 + heroSlides.length) % heroSlides.length
        : 0
    );
  };

  const showNextHeroSlide = () => {
    setHeroSlideIndex((current) =>
      heroSlides.length ? (current + 1) % heroSlides.length : 0
    );
  };

  useEffect(() => {
    if (heroSlides.length <= 1) {
      setHeroSlideIndex(0);
      return;
    }

    const timer = window.setInterval(() => {
      setHeroSlideIndex((current) => (current + 1) % heroSlides.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlideIndex >= heroSlides.length) {
      setHeroSlideIndex(0);
    }
  }, [heroSlideIndex, heroSlides.length]);

  const tagEnabled = {
    bestseller: home.bestseller_enabled,
    "new-arrival": home.new_arrivals_enabled,
    festive: home.festive_enabled,
  };

  return (
    <div>
      <SEO
        title="Sacred weaves. Everyday grace."
        noSuffix
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Nityavastra",
            "url": typeof window !== "undefined" ? window.location.origin : "",
            "logo": "https://customer-assets-lqy194kg.emergentagent.net/job_nityavastra-shop/artifacts/dirh3az1_Updated%20Logo%20Nityavastra.png",
            "description": "Handwoven sarees, bedsheets and home essentials from Banaras and Jaipur weavers.",
            "address": { "@type": "PostalAddress", "streetAddress": "Kalyani Nagar Rd", "addressLocality": "Bhubaneswar", "postalCode": "751019", "addressCountry": "IN" },
            "contactPoint": { "@type": "ContactPoint", "telephone": "+91-8281037035", "contactType": "customer service", "email": "hello@nityavastra.com", "areaServed": "IN" },
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Nityavastra",
            "url": typeof window !== "undefined" ? window.location.origin : "",
            "potentialAction": {
              "@type": "SearchAction",
              "target": (typeof window !== "undefined" ? window.location.origin : "") + "/shop?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        ]}
      />
      {/* Modern customer landing hero — adapted from Bolt design */}
      {home.hero_enabled && (
<section className="relative overflow-hidden bg-[#F5F2EC]">
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden select-none"
          aria-hidden="true"
        >
          <span
            className="font-serif font-semibold leading-none text-[#EAE5DE]"
            style={{ fontSize: "clamp(100px, 20vw, 260px)", letterSpacing: "-0.04em" }}
          >
            DRAPE
          </span>
        </div>

        <div
          className="pointer-events-none absolute right-[10%] top-[15%] h-[420px] w-[280px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #E2006A 0%, transparent 70%)" }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute bottom-[8%] left-[3%] h-[300px] w-[220px] rounded-full opacity-15 blur-3xl"
          style={{ background: "radial-gradient(circle, #0046C0 0%, transparent 70%)" }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute left-[22%] top-[26%] h-[180px] w-[180px] rounded-full opacity-15 blur-3xl"
          style={{ background: "radial-gradient(circle, #FF8200 0%, transparent 70%)" }}
          aria-hidden="true"
        />

        <div className="relative z-10 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-10">
          <div className="grid min-h-[78vh] items-center gap-10 py-10 lg:grid-cols-[1fr_460px_1fr] lg:gap-8 lg:py-14 xl:grid-cols-[1fr_520px_1fr]">

            <div className="order-2 flex flex-col justify-center lg:order-1">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-px w-8 bg-[#E2006A]" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#E2006A]">
                  {home.hero_eyebrow}
                </span>
              </div>

              <h1
                className="font-serif font-semibold leading-[0.9] text-[#161412]"
                style={{ fontSize: "clamp(54px, 7vw, 96px)" }}
              >
                {home.hero_title}
              </h1>

              <p className="mt-8 max-w-xs text-[15px] font-light leading-relaxed text-[#7A7068] lg:text-base">
                {home.hero_subtitle}
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <Link
                  to={home.hero_primary_cta_link || "/shop?category=sarees"}
                  className="inline-flex items-center justify-center gap-2 border border-[#161412] bg-[#161412] px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-[#E2006A] hover:border-[#E2006A]"
                >
                  {home.hero_primary_cta_text}
                  <ArrowRight className="h-4 w-4" />
                </Link>

                <Link
                  to={home.hero_secondary_cta_link || "/shop"}
                  className="inline-flex items-center justify-center gap-2 border border-transparent px-7 py-3.5 text-[12px] font-medium uppercase tracking-[0.2em] text-[#7A7068] transition-all duration-300 hover:border-[#E2006A]/20 hover:text-[#E2006A]"
                >
                  {home.hero_secondary_cta_text}
                </Link>
              </div>
            </div>

            <div className="order-1 flex justify-center lg:order-2">
              <div
                ref={heroImageRef}
                className="relative w-full max-w-[420px] transition-transform duration-75 ease-linear lg:max-w-none"
              >
                <div
                  className="relative aspect-[2/3] overflow-hidden"
                  onTouchStart={(e) => {
                    heroTouchStartX.current = e.touches?.[0]?.clientX ?? null;
                  }}
                  onTouchEnd={(e) => {
                    if (heroTouchStartX.current == null) return;

                    const endX = e.changedTouches?.[0]?.clientX ?? heroTouchStartX.current;
                    const distance = endX - heroTouchStartX.current;

                    if (Math.abs(distance) > 45) {
                      if (distance < 0) {
                        showNextHeroSlide();
                      } else {
                        showPreviousHeroSlide();
                      }
                    }

                    heroTouchStartX.current = null;
                  }}
                >
                  {heroSlides.map((image, index) => (
                    <img
                      key={`${image}-${index}`}
                      src={absoluteImageUrl(image)}
                      alt={`Nityavastra hero slide ${index + 1}`}
                      className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
                        index === heroSlideIndex ? "opacity-100" : "opacity-0"
                      }`}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  ))}
                  <div
                    className="absolute inset-x-0 bottom-0 h-1/3"
                    style={{
                      background: "linear-gradient(to top, rgba(226,0,106,0.25), transparent)",
                    }}
                  />

                  {heroSlides.length > 1 && (
                    <>
                      <button
                        type="button"
                        aria-label="Previous hero image"
                        onClick={showPreviousHeroSlide}
                        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-black/25 text-2xl leading-none text-white backdrop-blur-sm transition hover:bg-black/45"
                      >
                        ‹
                      </button>

                      <button
                        type="button"
                        aria-label="Next hero image"
                        onClick={showNextHeroSlide}
                        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-black/25 text-2xl leading-none text-white backdrop-blur-sm transition hover:bg-black/45"
                      >
                        ›
                      </button>

                      <div
                        className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/20 px-3 py-2 backdrop-blur-sm"
                        aria-label="Hero slides"
                      >
                        {heroSlides.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            aria-label={`Show hero image ${index + 1}`}
                            onClick={() => setHeroSlideIndex(index)}
                            className={`h-2.5 rounded-full border border-white transition-all duration-300 ${
                              index === heroSlideIndex
                                ? "w-7 bg-white"
                                : "w-2.5 bg-white/30 hover:bg-white/70"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="absolute -bottom-4 -right-2 rotate-3 bg-[#E2006A] px-5 py-3 text-white sm:-right-4 lg:-right-8">
                  <p className="font-serif text-lg font-medium italic leading-tight">
                    {home.hero_badge}
                  </p>
                </div>

                <div
                  className="absolute -left-4 -top-4 h-24 w-24 border border-[#E2006A]/40 lg:-left-6"
                  aria-hidden="true"
                />
              </div>
            </div>

            <div className="order-3 hidden flex-col items-end justify-center gap-6 lg:flex">
              <div className="relative aspect-[2/3] w-[200px] overflow-hidden xl:w-[240px]">
                <img
                  src={
                    absoluteImageUrl(home.hero_secondary_image) ||
                    "https://images.pexels.com/photos/33150440/pexels-photo-33150440.jpeg?auto=compress&cs=tinysrgb&h=900&w=600"
                  }
                  alt="Modern saree styling"
                  className="h-full w-full object-cover object-top transition-transform duration-700 hover:scale-105"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-[#161412]/10" />
              </div>

              <div className="max-w-[220px] text-right">
                <p className="mb-2 text-[11px] uppercase tracking-[0.25em] text-[#7A7068]">
                  Six Yards of
                </p>
                <p className="font-serif text-4xl font-semibold leading-none text-[#161412]">{home.hero_caption}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden border-y border-[#E8E3DB] bg-[#FAF8F4] py-3">
          <div
            className="flex w-max whitespace-nowrap"
            style={{ animation: "nvHomeMarquee 24s linear infinite" }}
          >
            {[0, 1].map((copy) => (
              <div key={copy} className="flex items-center">
                {[
                  "COTTON SAREES", "✦", "HANDLOOM", "✦", "SILK SAREES", "✦",
                  "NEW ARRIVALS", "✦", "BANARASI", "✦", "PRINTED SAREES", "✦",
                  "EVERYDAY EDIT", "✦", "FESTIVE", "✦", "KANJIVARAM", "✦",
                  "LINEN", "✦", "CHIFFON", "✦", "SUSTAINABLE", "✦"
                ].map((item, index) => (
                  <span
                    key={`${copy}-${index}`}
                    className={`px-4 text-[11px] font-medium tracking-[0.3em] ${
                      item === "✦" ? "text-[#E2006A]" : "text-[#7A7068]"
                    }`}
                  >
                    {item}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes nvHomeMarquee {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          @media (prefers-reduced-motion: reduce) {
            [style*="nvHomeMarquee"] { animation: none !important; }
          }
        `}</style>
      </section>
      )}

      {/* Trust bar */}
      {home.trust_strip_enabled && (
<section className="border-y border-[#E7E5E4] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRUST.map((t) => (
            <div key={t.title} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F5F5F4] flex items-center justify-center shrink-0">
                <t.icon className="h-5 w-5 text-[#7C1F30]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#2A1508]">{t.title}</div>
                <div className="text-xs text-[#78716C]">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Categories bento */}
      {home.categories_enabled && (
<section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 md:py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="uppercase text-xs tracking-[0.3em] text-[#7C1F30] mb-3">
              {home.categories_subtitle}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2A1508] max-w-lg leading-tight">
              {home.categories_title}
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm hover:text-[#7C1F30] transition-colors">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {categories.slice(0, 3).map((c) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-lg"
            >
              <img
                src={
  absoluteImageUrl(c.image) ||
  ({
    sarees: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=900&q=85",
    "daily-wear": "https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?auto=format&fit=crop&w=900&q=85",
    "home-essentials": "https://images.pexels.com/photos/6480707/pexels-photo-6480707.jpeg?auto=compress&cs=tinysrgb&w=900"
  }[c.slug])
}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2A1508]/80 via-[#2A1508]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <h3 className="font-serif text-3xl text-white mb-2">{c.name}</h3>
                <span className="inline-flex items-center gap-2 text-white/90 text-sm">
                  Explore <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {home.shop_by_mood_enabled && (
        <section
          className="bg-[#161412] text-white"
          data-testid="section-shop-by-mood"
        >
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 md:py-24">
            <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.34em] text-[#FF70B2]">
                  Discover Your Expression
                </p>
                <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
                  {home.shop_by_mood_title}
                </h2>
              </div>
              <p className="max-w-md text-sm leading-relaxed text-white/60">
                Style the saree your way — soft, bold, effortless or celebratory.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  image: absoluteImageUrl(home.shop_by_mood_image_1),
                  fallback:
                    "https://images.pexels.com/photos/32982848/pexels-photo-32982848.jpeg?auto=compress&cs=tinysrgb&w=900",
                  title: "Soft & Everyday",
                  link: "/shop?category=sarees",
                },
                {
                  image: absoluteImageUrl(home.shop_by_mood_image_2),
                  fallback:
                    "https://images.pexels.com/photos/33150440/pexels-photo-33150440.jpeg?auto=compress&cs=tinysrgb&w=900",
                  title: "Bold & Contemporary",
                  link: "/shop",
                },
                {
                  image: absoluteImageUrl(home.shop_by_mood_image_3),
                  fallback:
                    "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=900&q=85",
                  title: "Festive & Statement",
                  link: "/shop?tag=festive",
                },
              ].map((mood, index) => (
                <Link
                  key={mood.title}
                  to={mood.link}
                  className={`group relative overflow-hidden ${
                    index === 1 ? "md:translate-y-8" : ""
                  }`}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={mood.image || mood.fallback}
                      alt={mood.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.3em] text-white/60">
                      Mood {String(index + 1).padStart(2, "0")}
                    </p>
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-serif text-2xl sm:text-3xl">
                        {mood.title}
                      </h3>
                      <ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tag-based product sections */}
      {TAG_SECTIONS.map(({ tag, title, eyebrow }) =>
        tagEnabled[tag] && tagSections[tag] && tagSections[tag].length > 0 ? (
          <section key={tag} className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-20" data-testid={`section-${tag}`}>
            <div className="text-center mb-10">
              <p className="uppercase text-xs tracking-[0.3em] text-[#7C1F30] mb-3">{eyebrow}</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#2A1508] leading-tight">{title}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-4 sm:gap-y-10 md:gap-x-6 md:gap-y-12 lg:gap-x-8 lg:gap-y-14">
              {tagSections[tag].slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        ) : null
      )}

      {home.textile_story_enabled && (
        <section
          className="overflow-hidden bg-[#F5F2EC]"
          data-testid="section-textile-story"
        >
          <div className="mx-auto grid max-w-[1440px] lg:grid-cols-2">
            <div className="relative min-h-[480px] overflow-hidden lg:min-h-[650px]">
              <img
                src={
                  absoluteImageUrl(home.textile_story_image) ||
                  "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=85"
                }
                alt="Indian textile craftsmanship"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#161412]/45 via-transparent to-transparent" />

              <div className="absolute bottom-6 left-6 border border-white/50 bg-black/20 px-4 py-2 backdrop-blur-sm">
                <span className="text-[9px] font-semibold uppercase tracking-[0.3em] text-white">
                  Craft · Cloth · Culture
                </span>
              </div>
            </div>

            <div className="flex items-center px-6 py-16 sm:px-10 lg:px-16 lg:py-20 xl:px-24">
              <div className="max-w-xl">
                <div className="mb-7 flex items-center gap-3">
                  <span className="h-px w-10 bg-[#E2006A]" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.34em] text-[#E2006A]">
                    The Textile Story
                  </span>
                </div>

                <h2 className="font-serif text-4xl font-semibold leading-[0.95] text-[#161412] sm:text-5xl lg:text-6xl">
                  {home.textile_story_title}
                </h2>

                <p className="mt-8 text-base font-light leading-8 text-[#6F675F]">
                  {home.textile_story_text ||
                    "Every weave carries a story — of material, maker and the way you choose to wear it. Discover Indian textiles reimagined for everyday expression."}
                </p>

                <Link
                  to="/shop"
                  className="mt-10 inline-flex items-center gap-3 border-b border-[#161412] pb-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#161412] transition-colors hover:border-[#E2006A] hover:text-[#E2006A]"
                >
                  Explore the collection
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {home.community_enabled && (
        <section
          className="bg-white py-20 md:py-24"
          data-testid="section-community"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-8">
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.34em] text-[#E2006A]">
                  Community
                </p>
                <h2 className="font-serif text-4xl font-semibold leading-tight text-[#161412] sm:text-5xl">
                  {home.community_title}
                </h2>
              </div>

              <p className="max-w-md text-sm leading-relaxed text-[#78716C]">
                Real styling, personal expression and modern Indian drapes worn your way.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  image: absoluteImageUrl(home.community_image_1),
                  fallback:
                    "https://images.pexels.com/photos/32982848/pexels-photo-32982848.jpeg?auto=compress&cs=tinysrgb&w=900",
                  label: "Everyday",
                },
                {
                  image: absoluteImageUrl(home.community_image_2),
                  fallback:
                    "https://images.pexels.com/photos/33150440/pexels-photo-33150440.jpeg?auto=compress&cs=tinysrgb&w=900",
                  label: "Contemporary",
                },
                {
                  image: absoluteImageUrl(home.community_image_3),
                  fallback:
                    "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=900&q=85",
                  label: "Celebration",
                },
              ].map((item, index) => (
                <div
                  key={item.label}
                  className={`group relative overflow-hidden ${
                    index === 1 ? "md:-translate-y-6" : ""
                  }`}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={item.image || item.fallback}
                      alt={`Nityavastra community — ${item.label}`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 text-white">
                    <div>
                      <p className="text-[9px] font-semibold uppercase tracking-[0.28em] text-white/60">
                        Worn Your Way
                      </p>
                      <p className="mt-1 font-serif text-2xl">
                        {item.label}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#161412] transition-colors hover:text-[#E2006A]"
              >
                Shop the look
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
